import { fetchTyreActivityFromApi } from './activity-log.js';
import { parseSeasonPoolPayload } from '../utils/tyre-verify.js';
import {
  extractStartNumbersFromSeriesIndex,
  mergeSeasonReportCars,
  parseSeasonReportFleetPayload
} from '../utils/season-report.js';

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {number} startNumber
 * @param {typeof fetch} [fetchFn]
 * @returns {Promise<string[] | null>}
 */
async function fetchCarSeasonPool(apiBase, seriesName, startNumber, fetchFn = fetch) {
  try {
    const response = await fetchFn(
      `${ apiBase }/wheels/${ encodeURIComponent(seriesName) }/${ startNumber }/tyres`,
      { headers: { Accept: 'application/json' }, cache: 'no-store' }
    );

    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      return null;
    }

    return parseSeasonPoolPayload(await response.json());
  }
  catch {
    return null;
  }
}

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {typeof fetch} [fetchFn]
 * @returns {Promise<number[]>}
 */
async function discoverStartNumbers(apiBase, seriesName, fetchFn = fetch) {
  /** @type {Set<number>} */
  const numbers = new Set();

  try {
    const response = await fetchFn(
      `${ apiBase }/wheels/${ encodeURIComponent(seriesName) }`,
      { headers: { Accept: 'application/json' }, cache: 'no-store' }
    );

    if (response.ok) {
      for (const sn of extractStartNumbersFromSeriesIndex(await response.json())) {
        numbers.add(sn);
      }
    }
  }
  catch {
    /* non-fatal */
  }

  const activity = await fetchTyreActivityFromApi(apiBase, seriesName);

  if (activity.ok) {
    for (const entry of activity.entries) {
      if (entry.startNumber) {
        numbers.add(entry.startNumber);
      }
    }
  }

  return [...numbers].sort((a, b) => a - b);
}

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {number[]} startNumbers
 * @param {typeof fetch} [fetchFn]
 * @returns {Promise<{ startNumber: number, serials: string[] }[]>}
 */
async function fetchPoolsForStartNumbers(apiBase, seriesName, startNumbers, fetchFn = fetch) {
  const results = await Promise.all(
    startNumbers.map(async startNumber => {
      const serials = await fetchCarSeasonPool(apiBase, seriesName, startNumber, fetchFn);
      return { startNumber, serials: serials ?? [] };
    })
  );

  return mergeSeasonReportCars(results);
}

/**
 * Load all cars and their season tyre serials for scrollable fleet report.
 *
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {{ fetchFn?: typeof fetch }} [options]
 * @returns {Promise<{ ok: boolean, cars: { startNumber: number, serials: string[] }[] }>}
 */
export async function fetchSeasonReportFleet(apiBase, seriesName, { fetchFn = fetch } = {}) {
  if (!apiBase || !seriesName) {
    return { ok: false, cars: [] };
  }

  const enc = encodeURIComponent(seriesName);

  try {
    const aggregate = await fetchFn(`${ apiBase }/wheels/${ enc }/tyres`, {
      headers: { Accept: 'application/json' },
      cache:   'no-store'
    });

    if (aggregate.ok) {
      const cars = parseSeasonReportFleetPayload(await aggregate.json());

      if (cars && cars.some(car => car.serials.length > 0)) {
        return { ok: true, cars };
      }
    }
  }
  catch {
    /* try per-car fallback */
  }

  const startNumbers = await discoverStartNumbers(apiBase, seriesName, fetchFn);

  if (!startNumbers.length) {
    return { ok: false, cars: [] };
  }

  const cars = await fetchPoolsForStartNumbers(apiBase, seriesName, startNumbers, fetchFn);

  return {
    ok:   cars.some(car => car.serials.length > 0),
    cars
  };
}

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {{ keepActivity?: boolean, adminToken?: string, startNumber?: number|string, fetchFn?: typeof fetch }} [options]
 * @returns {Promise<{ ok: boolean, message?: string, result?: object }>}
 */
export async function resetTracksideSeriesData(apiBase, seriesName, {
  keepActivity = false,
  adminToken   = '',
  startNumber  = '',
  fetchFn      = fetch
} = {}) {
  if (!apiBase || !seriesName) {
    return { ok: false, message: 'API not configured.' };
  }

  if (!adminToken) {
    return { ok: false, message: 'Admin token missing — set it in Settings.' };
  }

  const params = new URLSearchParams();

  if (keepActivity) {
    params.set('keepActivity', 'true');
  }

  if (startNumber) {
    params.set('startNumber', String(startNumber));
  }

  const qs  = params.toString() ? `?${ params }` : '';
  const url = `${ apiBase }/wheels/${ encodeURIComponent(seriesName) }/reset${ qs }`;

  try {
    const response = await fetchFn(url, {
      method:  'DELETE',
      headers: {
        Accept:         'application/json',
        'X-Admin-Token': adminToken
      },
      cache: 'no-store'
    });

    let body = null;

    try {
      body = await response.json();
    }
    catch {
      body = null;
    }

    if (response.status === 403) {
      return { ok: false, message: 'Invalid admin token.' };
    }

    if (response.status === 503) {
      return { ok: false, message: 'Reset not enabled on server.' };
    }

    if (!response.ok) {
      return {
        ok:      false,
        message: body?.message || `Server returned ${ response.status }.`
      };
    }

    return { ok: true, result: body };
  }
  catch {
    return { ok: false, message: 'Could not reach the trackside API.' };
  }
}
